import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useSalon } from '../../contexts/SalonContext';
import { apiService } from '../../services/api';

import { QRCodeSVG } from 'qrcode.react';
import PlanGate from '../PlanGate';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { notifyNewBooking, requestPermission } = useNotification();
  const { currentBusiness, updateBusiness } = useSalon();
  const [shopName, setShopName] = useState('');
  const [isEditingShopName, setIsEditingShopName] = useState(false);
  const [tempShopName, setTempShopName] = useState('');
  const [savingShopName, setSavingShopName] = useState(false);

  // State cho dữ liệu từ API
  const [dashboardStats, setDashboardStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [todayBookings, setTodayBookings] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [services, setServices] = useState([]);
  const [todaySchedules, setTodaySchedules] = useState([]); // lịch làm việc hôm nay
  const [currentTime, setCurrentTime] = useState(new Date()); // Timeline giờ thực
  const [selectedDate, setSelectedDate] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [tempSelectedDate, setTempSelectedDate] = useState(() => new Date().toLocaleDateString('en-CA'));
  const STAFF_PER_PAGE = 5;
  const [zoomLevel, setZoomLevel] = useState(1);
  const [staffPage, setStaffPage] = useState(0);
  const pinchRef = useRef({ active: false, initialDist: 0, initialZoom: 1 });
  const zoomLevelRef = useRef(1);
  const swipeRef = useRef({ startX: 0, startY: 0, active: false });

  // Keep ref in sync with state (needed inside native event listeners)
  useEffect(() => {
    zoomLevelRef.current = zoomLevel;
  }, [zoomLevel]);

  // Callback ref: runs every time the DOM element mounts/unmounts
  // This guarantees the element exists when we attach listeners
  const gridCallbackRef = useRef(null);
  const setGridRef = (node) => {
    // Cleanup old element
    if (gridCallbackRef.current) {
      gridCallbackRef.current.removeEventListener('touchstart', onTouchStart);
      gridCallbackRef.current.removeEventListener('touchmove', onTouchMove);
      gridCallbackRef.current.removeEventListener('touchend', onTouchEnd);
    }
    // Attach to new element
    if (node) {
      node.addEventListener('touchstart', onTouchStart, { passive: false });
      node.addEventListener('touchmove', onTouchMove, { passive: false });
      node.addEventListener('touchend', onTouchEnd, { passive: true });
    }
    gridCallbackRef.current = node;
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      pinchRef.current = { active: true, initialDist: dist, initialZoom: zoomLevelRef.current };
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current.active) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const ratio = dist / pinchRef.current.initialDist;
      let next = pinchRef.current.initialZoom * ratio;
      if (next < 0.5) next = 0.5;
      if (next > 2.5) next = 2.5;
      setZoomLevel(next);
    }
  };

  const onTouchEnd = () => {
    pinchRef.current.active = false;
  };

  // State cho loading và error
  const [loading, setLoading] = useState({
    stats: true,
    employees: true,
    bookings: true,
    activities: true,
    services: true
  });
  const [error, setError] = useState(null);

  // State cho modal
  const [showAddBookingModal, setShowAddBookingModal] = useState(false);
  const [showOnlineBookingModal, setShowOnlineBookingModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [viewBookingDetail, setViewBookingDetail] = useState(null);
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [assignIsRequested, setAssignIsRequested] = useState(false);

  // State cho form booking
  const [newBooking, setNewBooking] = useState({
    customerName: '',
    customerPhone: '',
    serviceType: '',
    selectedServiceIds: [],
    employeeId: '',
    bookingDate: new Date().toLocaleDateString('en-CA'),
    time: '09:00',
    numberOfPeople: 1,
    specialRequests: ''
  });

  const [onlineBooking, setOnlineBooking] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    serviceType: '', // Main service
    selectedServiceIds: [], // Multiple services
    bookingDate: new Date().toLocaleDateString('en-CA'),
    time: '09:00',
    numberOfPeople: 1,
    specialRequests: ''
  });

  // Lấy tất cả dữ liệu dashboard từ API
  useEffect(() => {
    const fetchDashboardData = async (silent = false) => {
      try {
        setError(null);
        // Use selectedDate instead of constant today
        const today = selectedDate;

        // Lấy stats
        const statsResponse = await apiService.getDashboardStats({ date: today });
        setDashboardStats(statsResponse.data || statsResponse);

        // Lấy danh sách nhân viên
        const employeesResponse = await apiService.getEmployees({ status: 'active' });
        const empData = employeesResponse.data ?? employeesResponse;
        setEmployees(Array.isArray(empData) ? empData : []);

        // Lấy booking hôm nay
        const bookingsResponse = await apiService.getBookings({ date: today });
        const bkData = bookingsResponse.data ?? bookingsResponse;
        setTodayBookings(Array.isArray(bkData) ? bkData : []);

        // Set recent activities from the real Activity audit log
        const statsData = statsResponse.data || statsResponse;
        if (statsData?.recentActivities && Array.isArray(statsData.recentActivities)) {
          setRecentActivities(statsData.recentActivities.map(a => ({
            id: a._id,
            type: a.type === 'checkin' ? 'checkin' : a.type === 'checkout' ? 'checkout' : a.type === 'login' ? 'login' : 'booking',
            description: a.description,
            createdAt: a.createdAt
          })));
        } else {
          setRecentActivities([]);
        }

        if (!silent) {
          setLoading(prev => ({
            ...prev,
            stats: false,
            employees: false,
            bookings: false,
            activities: false
          }));
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError(t('dashboard.error.loadFailed'));
        if (!silent) {
          setLoading(prev => ({
            ...prev,
            stats: false,
            employees: false,
            bookings: false,
            activities: false
          }));
        }
      }
    };

    fetchDashboardData();

    // Safety timeout: nếu sau 10s mà vẫn loading thì force tắt
    const safetyTimeout = setTimeout(() => {
      setLoading({
        stats: false,
        employees: false,
        bookings: false,
        activities: false,
        services: false
      });
    }, 10000);

    // Refresh data mỗi 30 giây silently
    const interval = setInterval(() => fetchDashboardData(true), 30000);
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => {
      clearTimeout(safetyTimeout);
      clearInterval(interval);
      clearInterval(clockInterval);
    };
  }, [selectedDate]);

  // Lấy lịch làm việc hôm nay
  useEffect(() => {
    const fetchTodaySchedules = async () => {
      try {
        const res = await apiService.getSchedules({ startDate: selectedDate, endDate: selectedDate });
        const data = res?.data || [];
        setTodaySchedules(Array.isArray(data) ? data : []);
      } catch (e) {
        setTodaySchedules([]);
      }
    };
    fetchTodaySchedules();
  }, [selectedDate]);

  // Lấy services từ API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const servicesResponse = await apiService.getServices();
        const servicesData = servicesResponse.data ?? servicesResponse;
        setServices(Array.isArray(servicesData) ? servicesData : []);
        setLoading(prev => ({ ...prev, services: false }));
      } catch (error) {
        console.error('Error fetching services:', error);
        setServices([]);
        setLoading(prev => ({ ...prev, services: false }));
      }
    };

    fetchServices();
  }, []);

  // Sync shopName from server whenever currentBusiness changes (login, switch business)
  useEffect(() => {
    if (currentBusiness?.name) {
      setShopName(currentBusiness.name);
    }
  }, [currentBusiness]);

  // Bắt đầu chỉnh sửa tên salon
  const startEditingShopName = () => {
    setTempShopName(shopName);
    setIsEditingShopName(true);
  };

  // Lưu tên salon lên server
  const saveShopName = async () => {
    const trimmed = tempShopName.trim();
    if (!trimmed) {
      alert(t('dashboard.shopName.emptyError'));
      return;
    }
    if (!currentBusiness?.id) {
      // No business yet — just update local state
      setShopName(trimmed);
      setIsEditingShopName(false);
      return;
    }
    try {
      setSavingShopName(true);
      await updateBusiness(currentBusiness.id, { name: trimmed });
      setShopName(trimmed);
      setIsEditingShopName(false);
    } catch (err) {
      alert(t('dashboard.shopName.saveError'));
      console.error('Save shop name error:', err);
    } finally {
      setSavingShopName(false);
    }
  };

  // Hủy chỉnh sửa tên salon
  const cancelEditingShopName = () => {
    setIsEditingShopName(false);
  };

  // Mo modal them booking tu nhan vien
  const openAddBookingModal = () => {
    setNewBooking({
      customerName: '',
      customerPhone: '',
      serviceType: '',
      selectedServiceIds: [],
      employeeId: '',
      bookingDate: new Date().toLocaleDateString('en-CA'),
      time: '09:00',
      numberOfPeople: 1,
      specialRequests: ''
    });
    setShowAddBookingModal(true);
  };

  // Mở modal booking online
  const openOnlineBookingModal = () => {
    if (services.length === 0) {
      alert(t('dashboard.error.noServices'));
      return;
    }

    setOnlineBooking({
      customerName: '',
      customerPhone: '',
      customerEmail: '',
      serviceType: services[0]?.name || '',
      bookingDate: new Date().toLocaleDateString('en-CA'),
      time: '09:00',
      numberOfPeople: 1,
      specialRequests: ''
    });
    setShowOnlineBookingModal(true);
  };

  // Thêm booking mới từ nhân viên
  const handleAddBooking = async () => {
    if (!newBooking.customerName.trim() || !newBooking.customerPhone.trim()) {
      alert(t('dashboard.addBooking.validationName'));
      return;
    }
    if (newBooking.selectedServiceIds.length === 0) {
      alert(t('dashboard.addBooking.validationService'));
      return;
    }
    if (!newBooking.employeeId) {
      alert(t('dashboard.addBooking.validationEmployee'));
      return;
    }

    // Kiem tra businessId
    const bizId = localStorage.getItem('currentBusinessId');
    if (!bizId) {
      alert(t('dashboard.addBooking.validationBiz'));
      return;
    }

    try {
      const selectedServices = services.filter(s =>
        newBooking.selectedServiceIds.map(String).includes(String(s.id || s._id))
      );
      const serviceType = selectedServices.map(s => s.name).join(', ') || newBooking.serviceType || 'Dich vu';

      const bookingData = {
        customerName: newBooking.customerName.trim(),
        customerPhone: newBooking.customerPhone.trim(),
        customerEmail: '',
        serviceType,
        serviceIds: newBooking.selectedServiceIds.map(String),
        services: selectedServices.map(s => s.name),
        employeeId: String(newBooking.employeeId),
        bookingDate: newBooking.bookingDate,
        time: newBooking.time,
        numberOfPeople: parseInt(String(newBooking.numberOfPeople)) || 1,
        specialRequests: newBooking.specialRequests || '',
        source: 'manual',
        status: 'confirmed'
      };

      console.log('[AddBooking] Sending data:', bookingData);
      console.log('[AddBooking] BusinessId in localStorage:', bizId);

      const result = await apiService.createBooking(bookingData);
      console.log('[AddBooking] Success:', result);

      // Refresh today bookings
      const today = new Date().toLocaleDateString('en-CA');
      const bookingsResponse = await apiService.getBookings({ date: today });
      const bkData = bookingsResponse.data ?? bookingsResponse;
      setTodayBookings(Array.isArray(bkData) ? bkData : []);

      // Refresh stats
      const statsResponse = await apiService.getDashboardStats({ date: today });
      setDashboardStats(statsResponse.data || statsResponse);

      // Reset form
      setNewBooking({
        customerName: '',
        customerPhone: '',
        serviceType: '',
        selectedServiceIds: [],
        employeeId: '',
        bookingDate: new Date().toLocaleDateString('en-CA'),
        time: '09:00',
        numberOfPeople: 1,
        specialRequests: ''
      });

      setShowAddBookingModal(false);
      alert(t('dashboard.addBooking.success'));

      // Notify
      const createdBooking = result?.data || result;
      if (createdBooking) notifyNewBooking(createdBooking);

    } catch (error) {
      console.error('[AddBooking] Error:', error);
      const msg = error.message || t('dashboard.addBooking.errorGeneric').replace(':', '');
      // Outside business hours error
      if (msg.includes('closed') || msg.includes('time must be between')) {
        alert(`⚠️ ${msg}\n\n${t('dashboard.addBooking.errorOutsideHours')}`);
      } else if (msg.includes('Business ID')) {
        alert(t('dashboard.addBooking.errorNoBiz'));
      } else {
        alert(`${t('dashboard.addBooking.errorGeneric')} ${msg}`);
      }
    }
  };

  // Xử lý booking từ khách online
  const handleOnlineBooking = async () => {
    if (!onlineBooking.customerName.trim() || !onlineBooking.customerPhone.trim()) {
      alert(t('dashboard.onlineBooking.validationName'));
      return;
    }

    try {
      const bookingData = {
        customerName: onlineBooking.customerName.trim(),
        customerPhone: onlineBooking.customerPhone.trim(),
        customerEmail: onlineBooking.customerEmail,
        serviceType: onlineBooking.serviceType,
        bookingDate: onlineBooking.bookingDate,
        time: onlineBooking.time,
        numberOfPeople: parseInt(onlineBooking.numberOfPeople) || 1,
        specialRequests: onlineBooking.specialRequests,
        status: 'pending'
      };

      await apiService.createBooking(bookingData);

      // Refresh current viewed date bookings
      const fetchDate = selectedDate;
      const bookingsResponse = await apiService.getBookings({ date: fetchDate });
      setTodayBookings(bookingsResponse.data || bookingsResponse);

      // Refresh stats
      const statsResponse = await apiService.getDashboardStats({ date: fetchDate });
      setDashboardStats(statsResponse.data || statsResponse);

      setShowOnlineBookingModal(false);
      alert(t('dashboard.onlineBooking.success'));

      // Notify relevant users about the new online booking
      const createdBooking = {
        id: Date.now(),
        customerName: bookingData.customerName,
        service: bookingData.serviceType,
        staffId: undefined, // Online bookings typically unassigned initially
        date: bookingData.bookingDate,
        time: bookingData.time
      };
      notifyNewBooking(createdBooking);

    } catch (error) {
      console.error('Error creating online booking:', error);
      alert(`${t('dashboard.onlineBooking.errorGeneric')} ${error.message || t('dashboard.onlineBooking.errorNoService')}`);
    }
  };

  // Mở modal phân công nhân viên cho booking
  const openAssignModal = (booking) => {
    setSelectedBooking(booking);
    setAssignEmployeeId(booking.employeeId?.toString() || '');
    setAssignIsRequested(booking.isRequestedStaff || false);
    setShowAssignModal(true);
  };

  // Lưu phân công nhân viên
  const handleAssignEmployee = async () => {
    if (!selectedBooking) return;
    try {
      const selectedEmp = employees.find(e => e.id?.toString() === assignEmployeeId);
      await apiService.updateBooking(selectedBooking.id || selectedBooking._id, {
        employeeId: assignEmployeeId || null,
        staffName: selectedEmp?.name || '',
        isRequestedStaff: assignIsRequested
      });
      // Refresh bookings for current view
      const fetchDate = selectedDate;
      const bookingsResponse = await apiService.getBookings({ date: fetchDate });
      setTodayBookings(bookingsResponse.data || bookingsResponse);
      setShowAssignModal(false);
      setSelectedBooking(null);
    } catch (error) {
      alert(`${t('dashboard.assignModal.assignError')} ${error.message}`);
    }
  };

  // Cập nhật trạng thái booking trực tiếp từ calendar card
  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    try {
      await apiService.updateBooking(bookingId, { status: newStatus });
      // Refresh bookings for current view
      const fetchDate = selectedDate;
      const bookingsResponse = await apiService.getBookings({ date: fetchDate });
      setTodayBookings(bookingsResponse.data || bookingsResponse);
    } catch (error) {
      alert(`${t('dashboard.updateStatus.errorPrefix')} ${error.message}`);
    }
  };

  // Cập nhật ngày/giờ của booking (chỉ owner, manager, receptionist)
  const handleUpdateBookingDateTime = async (bookingId, newDate, newTime) => {
    try {
      await apiService.updateBooking(bookingId, { bookingDate: newDate, time: newTime });
      // Refresh both the currently-viewed date and the new date (may differ)
      const refreshDate = newDate || selectedDate;
      const bookingsResponse = await apiService.getBookings({ date: selectedDate });
      setTodayBookings(bookingsResponse.data || bookingsResponse);
      setViewBookingDetail(prev => prev ? { ...prev, bookingDate: newDate, time: newTime } : prev);
    } catch (error) {
      alert(`${t('dashboard.updateStatus.errorPrefix') || 'Lỗi:'} ${error.message}`);
    }
  };

  // Xóa nhân viên
  const handleDeleteEmployee = async (employeeId) => {
    if (window.confirm(t('dashboard.deleteEmployee.confirm'))) {
      try {
        await apiService.deleteEmployee(employeeId);

        // Refresh employees list
        const employeesResponse = await apiService.getEmployees({ status: 'active' });
        setEmployees(employeesResponse.data || employeesResponse);

        // Refresh stats
        const fetchDate = selectedDate;
        const statsResponse = await apiService.getDashboardStats({ date: fetchDate });
        setDashboardStats(statsResponse.data || statsResponse);

        alert(t('dashboard.deleteEmployee.success'));

      } catch (error) {
        console.error('Error deleting employee:', error);
        alert(`${t('dashboard.deleteEmployee.errorPrefix')} ${error.message || t('dashboard.deleteEmployee.errorSuffix')}`);
      }
    }
  };

  // Format ngày tháng theo ngôn ngữ
  const getFormattedDate = () => {
    const now = new Date();

    // Capitalize first letter of the day for consistency
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const dateStr = now.toLocaleDateString(language === 'vi' ? 'vi-VN' : 'en-US', options);

    return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
  };

  const currentDateStr = getFormattedDate();

  // Lấy booking cho từng ô trong lịch
  const getBookingsForCell = (employeeId, timeSlot) => {
    const slotHour = parseInt(timeSlot.split(':')[0]);
    const slotMinute = parseInt(timeSlot.split(':')[1] || '0');
    const slotTotalMins = slotHour * 60 + slotMinute;

    return todayBookings.filter(booking => {
      // Xác định ID nhân viên
      let matchEmployee = false;
      if (employeeId === 'unassigned') {
        matchEmployee = (!booking.employeeId || booking.employeeId === 'unassigned');
      } else {
        const bEmpId = typeof booking.employeeId === 'object' ? booking.employeeId?._id : booking.employeeId;
        matchEmployee = String(bEmpId) === String(employeeId);
      }
      
      if (!matchEmployee || !booking.time) return false;

      // Xác định thời gian booking
      const bHour = parseInt(booking.time.split(':')[0]);
      const bMinute = parseInt(booking.time.split(':')[1] || '0');
      const bTotalMins = bHour * 60 + bMinute;

      // Booking thuộc về slot này nếu nằm trong khung 30 phút của slot
      return bTotalMins >= slotTotalMins && bTotalMins < slotTotalMins + 30;
    });
  };

  // Giờ làm việc — memoized, never regenerated unless deps change (static → [])
  const workingHours = useMemo(() => {
    const slots = [];
    for (let hour = 1; hour <= 24; hour++) {
      const hourStr = (hour === 24 ? 0 : hour).toString().padStart(2, '0');
      slots.push(`${hourStr}:00`);
      if (hour < 24) slots.push(`${hourStr}:30`);
    }
    return slots;
  }, []); // [] = computed once, never re-runs

  // Lấy màu sắc cho dịch vụ — useCallback so identity stays stable across renders
  const getServiceColor = useCallback((serviceType) => {
    const service = services.find(s => s.name === serviceType);
    return service ? service.color : '#CCCCCC';
  }, [services]);

  // Kiểm tra nhân viên có đang làm việc tại giờ đó không (dựa vào schedule)
  const isEmployeeWorkingAtTime = useCallback((employeeId, timeSlot) => {
    const empIdStr = String(employeeId);
    if (empIdStr === 'unassigned') return false;
    const schedule = todaySchedules.find(s => {
      const sid = String(s.employeeId || s.userId || '');
      return sid === empIdStr;
    });
    if (schedule && schedule.shifts && schedule.shifts.length > 0) {
      const shift = schedule.shifts[0];
      if (shift.type === 'off') return false;
      if (shift.startTime && shift.endTime)
        return timeSlot >= shift.startTime && timeSlot < shift.endTime;
    }
    return true; // default: working
  }, [todaySchedules]);

  // Kiểm tra loading state
  const isLoading = loading.stats || loading.employees || loading.bookings || loading.services;

  // Tạo khung giờ cho select (dùng chung logic với lịch)
  const timeSlots = workingHours;

  // ── Computed employees list (includes unassigned column if needed) ──────────
  const computedEmployees = useMemo(() => {
    const unassigned = todayBookings.filter(b => !b.employeeId || b.employeeId === 'unassigned');
    return unassigned.length > 0
      ? [{ id: 'unassigned', name: t('dashboard.empty.unassigned') || 'Chờ xếp thợ' }, ...employees]
      : employees;
  }, [todayBookings, employees, t]);

  // ── Staff pagination — memoized for stable references ────────────────────
  const totalStaffPages = useMemo(
    () => Math.max(1, Math.ceil(computedEmployees.length / STAFF_PER_PAGE)),
    [computedEmployees.length, STAFF_PER_PAGE]
  );
  const pagedEmployees = useMemo(
    () => computedEmployees.slice(staffPage * STAFF_PER_PAGE, (staffPage + 1) * STAFF_PER_PAGE),
    [computedEmployees, staffPage, STAFF_PER_PAGE]
  );

  // Navigate to adjacent day and reset to first staff page
  const goToNextDay = useCallback(() => {
    const d = new Date(selectedDate); d.setDate(d.getDate() + 1);
    const s = d.toLocaleDateString('en-CA');
    setSelectedDate(s); setTempSelectedDate(s); setStaffPage(0);
  }, [selectedDate]);

  const goToPrevDay = useCallback(() => {
    const d = new Date(selectedDate); d.setDate(d.getDate() - 1);
    const s = d.toLocaleDateString('en-CA');
    setSelectedDate(s); setTempSelectedDate(s); setStaffPage(0);
  }, [selectedDate]);

  // ── Single-finger swipe handlers ─────────────────────────────────────
  // useCallback: stable reference across re-renders — no unnecessary listener re-attachments
  const handleSwipeStart = useCallback((e) => {
    if (e.touches.length === 1) {
      swipeRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, active: true };
    }
  }, []); // no deps — only touches refs

  const handleSwipeEnd = useCallback((e) => {
    if (!swipeRef.current.active) return;
    swipeRef.current.active = false;
    if (e.changedTouches.length !== 1) return;
    const dx = e.changedTouches[0].clientX - swipeRef.current.startX;
    const dy = e.changedTouches[0].clientY - swipeRef.current.startY;
    if (Math.abs(dx) <= Math.abs(dy) || Math.abs(dx) < 60) return;
    if (dx < 0) {
      if (staffPage < totalStaffPages - 1) setStaffPage(p => p + 1);
      else goToNextDay();
    } else {
      if (staffPage > 0) setStaffPage(p => p - 1);
      else goToPrevDay();
    }
  }, [staffPage, totalStaffPages, goToNextDay, goToPrevDay]);

  return (
    <div className="dashboard-container">
      {/* Sidebar trái */}
      <div className="dashboard-sidebar">
        <div className="sidebar-header" style={
          currentBusiness?.settings?.logo
            ? {
                backgroundImage: `linear-gradient(to bottom, rgba(255,255,255,0.75), rgba(255,255,255,0.85)), url(${currentBusiness.settings.logo})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }
            : { background: '#fff' }
        }>

          {/* PHẦN NÀY GIỮ NGUYÊN 100% TỪ FILE GỐC */}
          {isEditingShopName ? (
            <div className="shop-name-editor">
              <input
                type="text"
                value={tempShopName}
                onChange={(e) => setTempShopName(e.target.value)}
                className="shop-name-input"
                autoFocus
                maxLength={30}
                placeholder={t('dashboard.shopName.placeholder')}
              />
              <div className="shop-name-actions">
                <button
                  onClick={saveShopName}
                  className="save-shop-name-btn"
                  title={t('dashboard.shopName.saveTitle')}
                  disabled={savingShopName}
                >
                  {savingShopName ? '...' : '✓'}
                </button>
                <button
                  onClick={cancelEditingShopName}
                  className="cancel-shop-name-btn"
                  title={t('dashboard.shopName.cancelTitle')}
                  disabled={savingShopName}
                >
                  ×
                </button>
              </div>
            </div>
          ) : (
            <div className="shop-name-display">
              <h1 className="shop-title">{shopName}</h1>
              <button
                onClick={startEditingShopName}
                className="edit-shop-name-btn"
                title={t('dashboard.shopName.editTitle')}
              >
                ✎
              </button>
            </div>
          )}
          <h2 className="shop-subtitle">{t('dashboard.greeting').replace('{name}', (user?.name || user?.username || 'User').split(' ')[0])}</h2>
        {/* Business ID — cho owner chia se voi nhan vien */}
        {currentBusiness && (currentBusiness.id || currentBusiness._id) && (
          <div style={{
            margin: '0 0 12px 0',
            padding: '10px 12px',
            background: 'rgba(255,255,255,0.08)',
            borderRadius: '10px',
            fontSize: '11px',
          }}>
            <div style={{
            color: 'rgba(255,255,255,0.75)', fontWeight: 600, marginBottom: '5px'
          }}>
            🔑 {t('dashboard.businessId.label')}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <code style={{
                flex: 1,
                fontSize: '10px',
                background: 'rgba(0,0,0,0.3)',
                padding: '4px 7px',
                borderRadius: '5px',
                color: '#a5f3fc',
                letterSpacing: '0.3px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
              }}>
                {currentBusiness.id || currentBusiness._id}
              </code>
              <button
                onClick={() => {
                  const bizId = String(currentBusiness.id || currentBusiness._id);
                  if (navigator.clipboard) {
                    navigator.clipboard.writeText(bizId).then(() => alert(t('dashboard.businessId.copySuccess')));
                  } else {
                    prompt(t('dashboard.businessId.copyPrompt'), bizId);
                  }
                }}
                style={{
                  flexShrink: 0,
                  padding: '4px 8px',
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  borderRadius: '5px',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '11px',
                }}
              >
                📋
              </button>
            </div>
          </div>
        )}

        </div>

        {/* Stats */}
        <PlanGate requiredPlan="pro" userPlan={user?.plan || 'basic'} showLocked={true}>
          <div className="sidebar-stats">
            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            {isLoading ? (
              <div className="loading-stats">
                <div className="loading-spinner"></div>
                <p>{t('employees.loading')}</p>
              </div>
            ) : dashboardStats ? (
              <>
                <div className="stat-item">
                  <span className="stat-label">{t('dashboard.stats.employees')}</span>
                  <span className="stat-value">{dashboardStats.employeeStats?.active || dashboardStats.employeeStats?.total || employees.length || 0}</span>
                </div>

                <div className="stat-item">
                  <span className="stat-label">{t('dashboard.stats.today')}</span>
                  <span className="stat-value">{currentDateStr.split(',')[0]}</span>
                </div>

                <div className="stat-item">
                  <span className="stat-label">{t('dashboard.stats.todayBookings')}</span>
                  <span className="stat-value">{dashboardStats.bookingStats?.today || todayBookings.length || 0}</span>
                </div>

                <div className="stat-item">
                  <span className="stat-label">{t('dashboard.stats.pendingBookings')}</span>
                  <span className="stat-value">{dashboardStats.quickSummary?.pendingApprovals || todayBookings.filter(b => b.status === 'pending').length || 0}</span>
                </div>


              </>
            ) : (
              <div className="empty-stats">
                <p>{t('dashboard.stats.noData')}</p>
              </div>
            )}
          </div>
        </PlanGate>

        {/* Danh sách nhân viên */}
        <div className="add-form">
          <h3 className="form-title">{t('dashboard.workingEmployees')}</h3>

          {loading.employees ? (
            <div className="loading">{t('common.loading')}</div>
          ) : employees.length > 0 ? (
            <div className="staff-list">
              <ul className="item-list">
                {employees.slice(0, 5).map(employee => (
                  <li key={employee.id} className="list-item">
                    <div className="employee-avatar">
                      {employee.name?.charAt(0) || 'NV'}
                    </div>
                    <div className="employee-info">
                      <span className="employee-name">{employee.name}</span>
                      <span className="employee-role">{employee.position || employee.role}</span>
                    </div>
                    {user?.role === 'owner' || user?.role === 'manager' ? (
                      <button
                        onClick={() => handleDeleteEmployee(employee.id)}
                        className="delete-btn"
                        aria-label="Delete"
                      >
                        ×
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
              {employees.length > 5 && (
                <div className="more-items">
                  + {employees.length - 5} {t('dashboard.more')}
                </div>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <p>{t('dashboard.empty.noEmployees')}</p>
            </div>
          )}
        </div>

        {/* Recent Activities */}
        <div className="add-form">
          <h3 className="form-title">{t('dashboard.recentActivities')}</h3>

          {recentActivities.length > 0 ? (
            <div className="activities-list">
              {recentActivities.slice(0, 10).map(activity => (
                <div key={activity.id} className="activity-item">
                  <div className="activity-icon">
                    {activity.type === 'login' ? '🔐' :
                     activity.type === 'checkin' ? '✅' :
                     activity.type === 'checkout' ? '🚪' :
                     activity.type === 'service_completed' ? '💰' :
                     activity.type === 'service_cancelled' ? '❌' :
                     activity.type === 'booking_created' ? '📅' :
                     activity.type === 'booking_updated' ? '✏️' : '📋'}
                  </div>
                  <div className="activity-content">
                    <div className="activity-description">{activity.description}</div>
                    <div className="activity-time">
                      {new Date(activity.createdAt).toLocaleString([], { 
                        month: 'short', day: '2-digit',
                        hour: '2-digit', minute: '2-digit' 
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-message">
              {t('dashboard.empty.noActivities')}
            </div>
          )}
        </div>

        {/* Các nút hành động */}
        <div className="sidebar-actions">
          <button className="add-new-btn" onClick={openAddBookingModal}>
            <span className="btn-icon">+</span>
            <span>{t('dashboard.actions.addBooking')}</span>
          </button>

          <div className="online-booking-group" style={{
            display: 'flex',
            gap: '8px',
            marginTop: '10px'
          }}>
            <button className="online-booking-btn" onClick={openOnlineBookingModal} style={{ flex: 1, margin: 0 }}>
              <span className="btn-icon">🌐</span>
              <span>{t('dashboard.actions.onlineBooking')}</span>
            </button>

            {/* QR Check-in button */}
            {currentBusiness?.bookingCode && (
              <PlanGate requiredPlan="pro" userPlan={user?.plan || 'basic'} showLocked={false}>
                <button
                  onClick={() => setShowQRModal(true)}
                  title="QR Check-in"
                  style={{
                    background: 'linear-gradient(135deg, #38a169, #276749)',
                    border: 'none',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '0 14px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap'
                  }}
                >
                  📱 QR
                </button>
              </PlanGate>
            )}

            {(user?.role === 'owner' || user?.role === 'manager' || user?.role === 'admin') && (
              <button
                className="online-booking-settings-btn"
                onClick={() => window.location.href = '/settings/booking'}
                title="Page Settings"
                style={{
                  background: '#f1f5f9',
                  border: '1px solid #e2e8f0',
                  color: '#475569',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '45px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  transition: 'all 0.2s'
                }}
              >
                ⚙️
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main content - Lịch */}
      <div className="dashboard-main">
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '500px' }}>
            <div className="loading-spinner" style={{ width: '44px', height: '44px', minWidth: '44px', minHeight: '44px', margin: '0 auto' }}></div>
            <p style={{ color: '#4a5568', fontWeight: 600, marginTop: '20px', fontSize: '1.2rem' }}>{t('schedule.loading') || 'Đang tải dữ liệu...'}</p>
          </div>
        ) : (
          <div className="calendar-container">
            <div className="calendar-header-bar">
              <div className="calendar-title">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <h2>{t('dashboard.calendar.title')}</h2>
                  {/* Prev / Next day */}
                  <button onClick={goToPrevDay} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '16px' }} title={language === 'vi' ? 'Ngày trước' : 'Previous day'}>‹</button>
                  <input
                    type="date"
                    value={tempSelectedDate}
                    onChange={(e) => setTempSelectedDate(e.target.value)}
                    style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', color: '#1e293b', fontSize: '14px', fontWeight: 600, outline: 'none', cursor: 'pointer' }}
                  />
                  <button onClick={() => { setSelectedDate(tempSelectedDate); setStaffPage(0); }} style={{ padding: '6px 14px', borderRadius: '8px', background: '#667eea', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                    {language === 'vi' ? 'Chọn' : 'Select'}
                  </button>
                  <button onClick={goToNextDay} style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '16px' }} title={language === 'vi' ? 'Ngày sau' : 'Next day'}>›</button>
                </div>
                <div className="today-bookings-count">
                  {todayBookings.length} {t('dashboard.calendar.bookingsCount')}
                </div>
              </div>
              <div className="calendar-controls">
                <button className="refresh-btn" onClick={() => window.location.reload()}>
                  ↻ {t('dashboard.refresh')}
                </button>
              </div>
            </div>

            {/* ── Staff page indicator dots ── */}
            {totalStaffPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', padding: '6px 0', userSelect: 'none' }}>
                <button onClick={goToPrevDay} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '12px', padding: '0 4px' }}>◀ {language === 'vi' ? 'Ngày' : 'Day'}</button>
                {Array.from({ length: totalStaffPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setStaffPage(i)}
                    style={{
                      width: staffPage === i ? '20px' : '8px',
                      height: '8px',
                      borderRadius: '4px',
                      border: 'none',
                      background: staffPage === i ? '#667eea' : '#cbd5e1',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      padding: 0
                    }}
                    title={`${language === 'vi' ? 'Trang' : 'Page'} ${i + 1}`}
                  />
                ))}
                <button onClick={goToNextDay} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '12px', padding: '0 4px' }}>{language === 'vi' ? 'Ngày' : 'Day'} ▶</button>
              </div>
            )}

            <div 
              ref={setGridRef}
              className="calendar-grid" 
              style={{ "--zoom-factor": zoomLevel, overflow: 'hidden' }}
              onTouchStart={handleSwipeStart}
              onTouchEnd={handleSwipeEnd}
            >
                  <>
              {/* Header với tên nhân viên */}
              <div className="calendar-header" style={{ gridTemplateColumns: `80px repeat(${Math.max(pagedEmployees.length, 1)}, 1fr)` }}>
            <div className="time-header">{t('dashboard.calendar.time')}</div>
            {pagedEmployees.length > 0 ? (
              pagedEmployees.map(employee => (
                <div key={employee.id} className="staff-header">
                  <div className="staff-name" style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                    {employee.id !== 'unassigned' && (
                      <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold', flexShrink: 0, overflow: 'hidden' }}>
                        {(employee.avatar || employee.userId?.avatar) ? (
                          <img src={employee.avatar || employee.userId?.avatar} alt={employee.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          (employee.name || "U").charAt(0).toUpperCase()
                        )}
                      </div>
                    )}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{(employee.name || '').split(' ')[0]}</span>
                  </div>
                  <div className="staff-booking-count" style={{ fontSize: '0.65rem' }}>
                    {employee.id === 'unassigned' 
                      ? todayBookings.filter(b => !b.employeeId || b.employeeId === 'unassigned').length
                      : todayBookings.filter(b => b.employeeId === employee.id || b.employeeId?._id === employee.id).length} bk
                  </div>
                </div>
              ))
            ) : (
              <div className="staff-header empty">
                <div className="staff-name">{t('dashboard.empty.noEmployees')}</div>
              </div>
            )}
          </div>
            {/* Nội dung lịch theo giờ */}
            <div className="calendar-body" style={{ gridTemplateColumns: `80px repeat(${Math.max(pagedEmployees.length, 1)}, 1fr)` }}>
              {/* Timeline chỉ báo giờ hiện tại */}
              {(() => {
                const isToday = selectedDate === tempSelectedDate && selectedDate === new Date().toLocaleDateString('en-CA');
                if (!isToday) return null;

                const currentHour = currentTime.getHours();
                const currentMinute = currentTime.getMinutes();
                const startH = workingHours.length > 0 ? parseInt(workingHours[0].split(':')[0]) : 9;
                
                if (currentHour >= startH && currentHour <= 24) {
                  const fractionalHour = (currentHour - startH) + (currentMinute / 60);
                  const timeLabel = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
                  
                  // Auto scroll
                  setTimeout(() => {
                     const el = document.querySelector('.current-time-line');
                     const container = document.querySelector('.dashboard-main');
                     if (el && container && !window.hasScrolled) {
                         el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                         window.hasScrolled = true;
                     }
                  }, 500);

                  return (
                    <div 
                      className="current-time-line" 
                      data-time={timeLabel} 
                      style={{ top: `calc(${fractionalHour} * var(--cell-height, 240px))` }} 
                    />
                  );
                }
                return null;
              })()}

            {/* Cột giờ */}
            <div className="time-column" style={{ width: '80px', minWidth: '80px' }}>
              {workingHours.map((timeSlot, index) => (
                <div
                  key={index}
                  className="time-slot"
                >
                  {timeSlot}
                </div>
              ))}
            </div>

            {/* Các cột nhân viên */}
            {pagedEmployees.length > 0 ? (
              pagedEmployees.map(employee => (
                <div key={employee.id} className="staff-column">
                  {workingHours.map((timeSlot, index) => {
                    const slotBookings = getBookingsForCell(employee.id, timeSlot);
                    const isWorking = isEmployeeWorkingAtTime(employee.id, timeSlot);

                    return (
                      <div
                        key={`${employee.id}-${index}`}
                        className={`time-cell${isWorking && slotBookings.length === 0 ? ' staff-working-slot' : ''}`}
                        style={isWorking && slotBookings.length === 0 ? {
                          background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%)',
                          borderLeft: '3px solid rgba(102,126,234,0.5)',
                          fontWeight: 700,
                          position: 'relative'
                        } : { position: 'relative' }}
                      >
                        {slotBookings.length > 0 ? (
                            slotBookings.map((booking, bIndex) => {
                                const bMinute = parseInt(booking.time.split(':')[1] || '0');
                                const offsetMinute = bMinute % 30; // Từ 0 đến 29
                                const marginPercent = (offsetMinute / 30) * 100;
                                
                                return (
                                  <div
                                    key={booking.id || bIndex}
                                    className="booking-item"
                                    onClick={() => setViewBookingDetail(booking)}
                                    style={{
                                      backgroundColor: getServiceColor(booking.serviceType),
                                      cursor: 'pointer',
                                      position: 'absolute',
                                      zIndex: 10,
                                      left: '5px',
                                      right: '5px',
                                      top: `${marginPercent}%`,
                                      minHeight: '40px', // Đảm bảo đủ để nhìn
                                      padding: '6px 8px',
                                      borderRadius: '8px',
                                      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                                      overflow: 'hidden'
                                    }}
                                    title={t('dashboard.calendar.viewDetailsTooltip') || "Nhấn để xem chi tiết"}
                                  >
                                    {/* Nội dung thu gọn để render đè absolute tốt hơn */}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <div className="booking-customer" style={{ fontSize: '0.8rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {booking.time} • {booking.customerName}
                                        {(booking.isRequestedStaff) && (
                                          <span title="Khách yêu cầu thợ" style={{ fontSize: '0.8rem', filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))' }}>
                                            📌
                                          </span>
                                        )}
                                      </div>
                                      <div style={{ fontSize: '0.6rem', background: 'rgba(0,0,0,0.15)', padding: '2px 4px', borderRadius: '4px', fontWeight: 600 }}>
                                        {booking.status?.toUpperCase()}
                                      </div>
                                    </div>
                                    <div className="booking-service" style={{ fontSize: '0.7rem', opacity: 0.9, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {booking.serviceType}
                                    </div>
                                  </div>
                                );
                            })
                        ) : (
                          <div className="empty-cell">
                            {isWorking && (
                              <div style={{
                                textAlign: 'center',
                                fontSize: '0.65rem',
                                color: 'rgba(102,126,234,0.7)',
                                fontWeight: 700,
                                letterSpacing: '0.5px'
                              }}>●</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            ) : (
              <div className="staff-column empty">
                {workingHours.map((timeSlot, index) => (
                  <div key={`empty-${index}`} className="time-cell">
                    <div className="empty-cell">-</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </>
        </div>

        {/* Legend - chú thích màu sắc */}
        {services.length > 0 && (
          <div className="booking-legend">
            <div className="legend-title">{t('sidebar.services')}:</div>
            <div className="legend-items">
              {services.map(service => (
                <div key={service.id} className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: service.color }}></div>
                  <span>{service.name} ({service.duration}{t('dashboard.min')} - ${service.price.toLocaleString()})</span>
                </div>
              ))}
              {/* Chú thích giờ làm việc từ Schedule */}
              {todaySchedules.length > 0 && (
                <div className="schedule-legend-item">
                  <div className="schedule-legend-dot"></div>
                  <span style={{ fontSize: '13px', color: '#667eea', fontWeight: 600 }}>
                    {language === 'vi' ? 'Giờ làm việc (theo lịch phân công)' : 'Scheduled working hours'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
          </div>
        )}
      </div>
      {showAddBookingModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{t('dashboard.modals.addBookingTitle')}</h3>
              <button
                className="modal-close"
                onClick={() => setShowAddBookingModal(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-form-group">
                <label>{t('bookings.table.customer')} *</label>
                <input
                  type="text"
                  placeholder={t('dashboard.modals.placeholders.customerName')}
                  value={newBooking.customerName}
                  onChange={(e) => setNewBooking({ ...newBooking, customerName: e.target.value })}
                  className="modal-input"
                />
              </div>

              <div className="modal-form-group">
                <label>{t('dashboard.modals.labels.phone')} *</label>
                <input
                  type="tel"
                  placeholder={t('dashboard.modals.placeholders.phone')}
                  value={newBooking.customerPhone}
                  onChange={(e) => setNewBooking({ ...newBooking, customerPhone: e.target.value })}
                  className="modal-input"
                />
              </div>

              <div className="modal-form-group">
                  <label className="form-label">{t('dashboard.calendar.selectEmployee')}</label>
                  <select
                    className="form-input"
                    value={newBooking.employeeId}
                    onChange={(e) => setNewBooking({ ...newBooking, employeeId: e.target.value })}
                  >
                    <option value="">{t('dashboard.calendar.anyEmployee')}</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name}</option>
                    ))}
                  </select>
              </div>

              <div className="modal-form-group">
                <label>{t('dashboard.modals.labels.service')} *</label>
                <div className="service-selection-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
                  {services.map(service => {
                    const svcId = String(service.id || service._id || '');
                    const isSelected = newBooking.selectedServiceIds.map(String).includes(svcId);
                    return (
                      <div
                        key={svcId}
                        onClick={() => {
                          const currentIds = newBooking.selectedServiceIds.map(String);
                          const nextIds = isSelected
                            ? currentIds.filter(id => id !== svcId)
                            : [...currentIds, svcId];
                          setNewBooking({
                            ...newBooking,
                            selectedServiceIds: nextIds,
                            serviceType: nextIds.length > 0
                              ? services.find(s => String(s.id || s._id) === nextIds[0])?.name || ''
                              : ''
                          });
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '16px',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          backgroundColor: isSelected ? (service.color || '#667eea') : '#f1f3f5',
                          color: isSelected ? '#fff' : '#495057',
                          border: `1px solid ${isSelected ? (service.color || '#667eea') : '#dee2e6'}`,
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {isSelected ? 'v ' : ''}{service.name}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="modal-form-group">
                <label>{t('dashboard.modals.labels.date')} *</label>
                <input
                  type="date"
                  value={newBooking.bookingDate}
                  onChange={(e) => setNewBooking({ ...newBooking, bookingDate: e.target.value })}
                  className="modal-input"
                />
              </div>

              <div className="modal-form-group">
                <label>{t('dashboard.modals.labels.time')} *</label>
                <select
                  value={newBooking.time}
                  onChange={(e) => setNewBooking({ ...newBooking, time: e.target.value })}
                  className="modal-select"
                >
                  {timeSlots.map((slot, index) => (
                    <option key={index} value={slot}>{slot}</option>
                  ))}
                </select>
              </div>

              <div className="modal-form-group">
                <label>{t('dashboard.modals.labels.people')}</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={newBooking.numberOfPeople}
                  onChange={(e) => setNewBooking({ ...newBooking, numberOfPeople: e.target.value })}
                  className="modal-input"
                />
              </div>

              <div className="modal-form-group">
                <label>{t('dashboard.modals.labels.notes')}</label>
                <textarea
                  placeholder={t('dashboard.modals.placeholders.notes')}
                  value={newBooking.specialRequests}
                  onChange={(e) => setNewBooking({ ...newBooking, specialRequests: e.target.value })}
                  className="modal-textarea"
                  rows="3"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="modal-btn cancel"
                onClick={() => setShowAddBookingModal(false)}
              >
                {t('dashboard.modals.buttons.cancel')}
              </button>
              <button
                className="modal-btn submit"
                onClick={handleAddBooking}
              >
                {t('dashboard.modals.buttons.submit')}
              </button>
            </div>
          </div>
        </div>
      )
      }

      {/* Modal booking online */}
      {
        showOnlineBookingModal && (
          <div className="modal-overlay">
            <div className="modal-content public-link-modal">
              <div className="modal-header">
                <h3>🌐 {t('dashboard.modals.publicLink.title')}</h3>
                <button
                  className="modal-close"
                  onClick={() => setShowOnlineBookingModal(false)}
                >
                  ×
                </button>
              </div>

              <div className="modal-body public-link-body" style={{ textAlign: 'center', padding: '30px 20px' }}>
                <div className="public-link-icon" style={{ fontSize: '48px', marginBottom: '20px' }}>🔗</div>
                <p style={{ color: '#4a5568', marginBottom: '25px', fontSize: '16px', lineHeight: '1.6' }}>
                  {t('dashboard.modals.publicLink.description')}
                </p>

                {currentBusiness?.bookingCode ? (
                  <>
                    <div className="url-display-box" style={{
                      background: '#f8fafc',
                      border: '1px dashed #cbd5e0',
                      padding: '12px',
                      borderRadius: '8px',
                      marginBottom: '15px',
                      wordBreak: 'break-all',
                      color: '#2d3748',
                      fontWeight: '500',
                      fontSize: '15px'
                    }}>
                      {`https://www.phanh.app/book/${currentBusiness.bookingCode}`}
                    </div>
                    <div style={{
                      background: '#edf2f7',
                      borderRadius: '6px',
                      padding: '8px 12px',
                      marginBottom: '20px',
                      fontSize: '13px',
                      color: '#718096'
                    }}>
                      Booking Code: <strong style={{ color: '#2d3748', letterSpacing: '2px' }}>{currentBusiness.bookingCode}</strong>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                      <button
                        className="modal-btn secondary"
                        onClick={() => {
                          navigator.clipboard.writeText(`https://www.phanh.app/book/${currentBusiness.bookingCode}`);
                          alert(t('dashboard.modals.publicLink.copySuccess'));
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        📋 {t('dashboard.modals.publicLink.copyBtn')}
                      </button>
                      <button
                        className="modal-btn submit"
                        onClick={() => window.open(`https://www.phanh.app/book/${currentBusiness.bookingCode}`, '_blank')}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        🚀 {t('dashboard.modals.publicLink.visitBtn')}
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{
                    background: '#fff5f5',
                    border: '1px solid #fed7d7',
                    padding: '16px',
                    borderRadius: '8px',
                    color: '#c53030',
                    fontSize: '14px'
                  }}>
                    <p style={{ margin: '0 0 12px' }}>⚠️ {t('dashboard.error.noBookingCode')}</p>
                    <button
                      onClick={async () => {
                        try {
                          await apiService.generateBookingCode(currentBusiness?.id || '');
                          window.location.reload();
                        } catch (err) {
                          alert('Could not generate code. Please try refreshing the page.');
                        }
                      }}
                      style={{
                        background: '#667eea',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 600
                      }}
                    >
                      🔄 {language === 'vi' ? 'Tạo mã booking' : 'Generate Booking Code'}
                    </button>
                  </div>
                )}
              </div>

              <div className="modal-footer" style={{ borderTop: 'none', justifyContent: 'center', paddingTop: '0' }}>
                <button
                  className="modal-btn cancel"
                  onClick={() => setShowOnlineBookingModal(false)}
                  style={{ width: '100px' }}
                >
                  {t('dashboard.modals.buttons.cancel')}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Modal phân công nhân viên - Booking Detail */}
      {viewBookingDetail && (
        <div className="modal-overlay" onClick={() => setViewBookingDetail(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)', borderRadius: '12px 12px 0 0' }}>
              <h3 style={{ color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                {t('dashboard.bookingDetail.title')}
                {viewBookingDetail.source === 'online' && (
                  <span style={{ fontSize: '0.7rem', background: 'rgba(255,255,255,0.25)', padding: '2px 8px', borderRadius: '10px' }}>
                    Online
                  </span>
                )}
              </h3>
              <button className="modal-close" style={{ color: '#fff' }} onClick={() => setViewBookingDetail(null)}>x</button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '2px' }}>{t('dashboard.bookingDetail.customer')}</div>
                  <div style={{ fontWeight: 700, color: '#1e293b' }}>{viewBookingDetail.customerName}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '2px' }}>{t('dashboard.bookingDetail.phone')}</div>
                  <div style={{ fontWeight: 600 }}>{viewBookingDetail.customerPhone || '--'}</div>
                </div>
                {viewBookingDetail.customerEmail && (
                  <div style={{ gridColumn: '1/-1' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '2px' }}>Email</div>
                    <div>{viewBookingDetail.customerEmail}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '2px' }}>{t('dashboard.bookingDetail.service')}</div>
                  <div style={{ fontWeight: 600 }}>{viewBookingDetail.serviceType}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '2px' }}>{t('dashboard.bookingDetail.people')}</div>
                  <div>{viewBookingDetail.numberOfPeople || 1}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '2px' }}>{t('dashboard.bookingDetail.date')}</div>
                  {['owner', 'manager', 'receptionist'].includes(user?.role) ? (
                    <input
                      type="date"
                      defaultValue={viewBookingDetail.bookingDate}
                      onBlur={async e => {
                        const newDate = e.target.value;
                        if (newDate && newDate !== viewBookingDetail.bookingDate) {
                          await handleUpdateBookingDateTime(
                            viewBookingDetail.id || viewBookingDetail._id,
                            newDate,
                            viewBookingDetail.time
                          );
                        }
                      }}
                      style={{
                        fontWeight: 600, fontSize: '0.9rem', border: '1.5px solid #667eea',
                        borderRadius: '7px', padding: '4px 8px', color: '#1e293b',
                        background: '#f0f4ff', cursor: 'pointer', outline: 'none', width: '100%'
                      }}
                    />
                  ) : (
                    <div style={{ fontWeight: 600 }}>{viewBookingDetail.bookingDate}</div>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '2px' }}>{t('dashboard.bookingDetail.time')}</div>
                  {['owner', 'manager', 'receptionist'].includes(user?.role) ? (
                    <input
                      type="time"
                      defaultValue={viewBookingDetail.time}
                      onBlur={async e => {
                        const newTime = e.target.value;
                        if (newTime && newTime !== viewBookingDetail.time) {
                          await handleUpdateBookingDateTime(
                            viewBookingDetail.id || viewBookingDetail._id,
                            viewBookingDetail.bookingDate,
                            newTime
                          );
                        }
                      }}
                      style={{
                        fontWeight: 600, fontSize: '0.9rem', border: '1.5px solid #667eea',
                        borderRadius: '7px', padding: '4px 8px', color: '#1e293b',
                        background: '#f0f4ff', cursor: 'pointer', outline: 'none', width: '100%'
                      }}
                    />
                  ) : (
                    <div style={{ fontWeight: 600 }}>{viewBookingDetail.time}</div>
                  )}
                </div>
                {viewBookingDetail.specialRequests && (
                  <div style={{ gridColumn: '1/-1' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>{t('dashboard.bookingDetail.notes')}</div>
                    <div style={{ background: '#f8fafc', padding: '8px 12px', borderRadius: '8px', fontStyle: 'italic', borderLeft: '3px solid #667eea' }}>
                      {viewBookingDetail.specialRequests}
                    </div>
                  </div>
                )}
                {viewBookingDetail.noteImages && viewBookingDetail.noteImages.length > 0 && (
                  <div style={{ gridColumn: '1/-1' }}>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>{t('dashboard.bookingDetail.attachedImages')}</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {viewBookingDetail.noteImages.map((imgUrl, idx) => (
                        <a key={idx} href={imgUrl} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                          <img 
                            src={imgUrl} 
                            alt={`attachment ${idx}`} 
                            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0' }} 
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '6px', fontWeight: 600 }}>{t('dashboard.bookingDetail.status')}</div>
                <select
                  value={viewBookingDetail.status}
                  onChange={async e => {
                    const ns = e.target.value;
                    await handleUpdateBookingStatus(viewBookingDetail.id || viewBookingDetail._id, ns);
                    setViewBookingDetail(prev => ({ ...prev, status: ns }));
                  }}
                  style={{
                    width: '100%', padding: '8px 12px', borderRadius: '8px',
                    border: '2px solid #e2e8f0', fontSize: '0.95rem', cursor: 'pointer', fontWeight: 700,
                    background: viewBookingDetail.status === 'pending' ? '#fff3cd' : viewBookingDetail.status === 'confirmed' ? '#d1fae5' : viewBookingDetail.status === 'completed' ? '#dbeafe' : '#fee2e2'
                  }}
                >
                  <option value="pending">{t('dashboard.statusOptions.pending')}</option>
                  <option value="confirmed">{t('dashboard.statusOptions.confirmed')}</option>
                  <option value="completed">{t('dashboard.statusOptions.completed')}</option>
                  <option value="cancelled">{t('dashboard.statusOptions.cancelled')}</option>
                </select>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                {t('dashboard.bookingDetail.staff')}:{' '}
                <strong>
                  {viewBookingDetail.employeeId
                    ? (employees.find(e => (e.id || e._id)?.toString() === viewBookingDetail.employeeId?.toString())?.name || viewBookingDetail.employeeId)
                    : t('dashboard.bookingDetail.unassigned')}
                </strong>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn cancel" onClick={() => setViewBookingDetail(null)}>{t('dashboard.bookingDetail.close')}</button>
              <button
                className="modal-btn submit"
                style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
                onClick={() => { openAssignModal(viewBookingDetail); setViewBookingDetail(null); }}
              >
                {t('dashboard.bookingDetail.assign')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal phân công nhân viên */}
      {showAssignModal && selectedBooking && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h3>👤 {t('dashboard.assignModal.title')}</h3>
              <button className="modal-close" onClick={() => setShowAssignModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {/* Booking info */}
              <div style={{
                background: '#f8fafc',
                borderRadius: '10px',
                padding: '14px 16px',
                marginBottom: '20px',
                borderLeft: '4px solid #667eea'
              }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', marginBottom: '6px' }}>
                  {selectedBooking.customerName}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '4px' }}>
                  🎯 {selectedBooking.serviceType}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '4px' }}>
                  🕐 {selectedBooking.time} — {selectedBooking.bookingDate || t('dashboard.assignModal.today')}
                </div>
                <div style={{ fontSize: '0.875rem' }}>
                  {selectedBooking.status === 'pending' ? t('dashboard.statusOptions.pending') :
                    selectedBooking.status === 'confirmed' ? t('dashboard.statusOptions.confirmed') : t('dashboard.statusOptions.cancelled')}
                </div>
              </div>

              {/* Employee select */}
              <div className="form-group">
                <label className="form-label">{t('dashboard.assignModal.selectStaff')}</label>
                <select
                  className="form-input"
                  value={assignEmployeeId}
                  onChange={e => setAssignEmployeeId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '2px solid #e2e8f0',
                    fontSize: '0.95rem',
                    background: '#f8fafc',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">{t('dashboard.assignModal.unassigned')}</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id?.toString()}>
                      {emp.name} {emp.position ? `(${emp.position})` : ''}
                    </option>
                  ))}
                </select>
                {assignEmployeeId && (
                  <div style={{ marginTop: '8px', fontSize: '0.82rem', color: '#667eea' }}>
                    {t('dashboard.assignModal.willAssign')} <strong>{employees.find(e => e.id?.toString() === assignEmployeeId)?.name}</strong>
                  </div>
                )}
                
                {/* Pin/Requested Staff toggle for Manager/Owner/Admin only */}
                {(user?.role === 'owner' || user?.role === 'manager' || user?.role === 'admin') && (
                  <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input 
                      type="checkbox" 
                      id="pin-checkbox"
                      checked={assignIsRequested}
                      onChange={e => setAssignIsRequested(e.target.checked)}
                      style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                    />
                    <label htmlFor="pin-checkbox" style={{ cursor: 'pointer', fontSize: '0.9rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      📌 {language === 'vi' ? 'Đánh dấu thợ được khách yêu cầu (Ghim)' : 'Mark staff as requested (Pin)'}
                    </label>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="modal-btn cancel"
                onClick={() => setShowAssignModal(false)}
              >
                {t('dashboard.assignModal.cancel')}
              </button>
              <button
                className="modal-btn submit"
                onClick={handleAssignEmployee}
                style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
              >
                {t('dashboard.assignModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── QR Check-in Modal ── */}
      {showQRModal && currentBusiness?.bookingCode && (
        <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #38a169, #276749)', borderRadius: '12px 12px 0 0' }}>
              <h3 style={{ color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                📱 QR Check-in / Check-out
              </h3>
            </div>
            <div className="modal-body" style={{ padding: '28px 24px' }}>
              <p style={{ color: '#4a5568', fontSize: '14px', marginBottom: '20px', lineHeight: '1.6' }}>
                {language === 'vi'
                  ? 'Khách hàng quét mã QR này để check-in khi đến và check-out khi hoàn tất để đánh giá.'
                  : 'Customers scan this QR code to check-in on arrival and check-out when done to leave a review.'}
              </p>

              {/* QR Code */}
              <div style={{
                display: 'inline-block', padding: '16px', background: '#fff',
                borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                marginBottom: '16px'
              }}>
                <QRCodeSVG
                  value={`https://www.phanh.app/checkin/${currentBusiness.bookingCode}`}
                  size={200}
                  level="H"
                  includeMargin={false}
                />
              </div>

              {/* URL display */}
              <div style={{
                background: '#f7fafc', border: '1px dashed #cbd5e0',
                borderRadius: '8px', padding: '8px 12px', marginBottom: '16px',
                fontSize: '12px', color: '#4a5568', wordBreak: 'break-all', fontFamily: 'monospace'
              }}>
                https://www.phanh.app/checkin/{currentBusiness.bookingCode}
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  className="modal-btn secondary"
                  onClick={() => {
                    navigator.clipboard.writeText(`https://www.phanh.app/checkin/${currentBusiness.bookingCode}`);
                    alert(language === 'vi' ? 'Đã copy link!' : 'Link copied!');
                  }}
                >
                  📋 {language === 'vi' ? 'Copy Link' : 'Copy Link'}
                </button>
                <button
                  className="modal-btn submit"
                  onClick={() => window.print()}
                  style={{ background: 'linear-gradient(135deg, #38a169, #276749)' }}
                >
                  🖨️ {language === 'vi' ? 'In QR' : 'Print QR'}
                </button>
              </div>
            </div>
            <div className="modal-footer" style={{ borderTop: 'none', justifyContent: 'center', paddingTop: 0 }}>
              <button className="modal-btn cancel" onClick={() => setShowQRModal(false)} style={{ width: '100px' }}>
                {t('dashboard.modals.buttons.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default Dashboard;
